'use client';

/**
 * Props
 * - form: `useTwoStepLeadForm()` 반환값 통째로 전달
 * - intro: step1 안내 문구 (미지정 시 기본 문구)
 * - step2: step2 `Form`에 전달되는 props (formName/submitLabel/onSuccess 등). leadType([SHARED_OFFICE])과 userCount는 자동 주입되므로 생략
 * - step2Footer: step2 하단 부가 문구 (예: 마케팅 동의 안내)
 * - className: 카드 wrapper 스타일 오버라이드. 다음 버튼 색상은 이 클래스에서 --btn-bg/--btn-hover/--btn-active 로 지정
 *
 * Data — step1 Input/FormTip/step2 fields는 공유 오피스 리드 폼으로 고정
 *
 * @example
 * const form = useTwoStepLeadForm();
 * <StepForm
 *   form={form}
 *   step2={{ formName: LEAD_FORM_NAME.PROMOTION, submitLabel: '...' }}
 * />
 */

import clsx from 'clsx';
import type { ReactNode } from 'react';

import type { useTwoStepLeadForm } from '@publishing/hooks/useTwoStepLeadForm';
import { PEOPLE_MAX, PEOPLE_MIN } from '@publishing/hooks/useTwoStepLeadForm';
import Input from '../../components/forms/input/Input';
import FormTip from '@publishing/modules/forms/common/FormTip';
import { Form, FORM_KEY } from '@publishing/modules/forms';
import type { FormField, FormProps } from '@publishing/modules/forms';
import { LEAD_TYPE } from '@/features/lead-form/constants/leadType';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick, toBtnName } from '@/shared/utils/gtm';
import { Button } from '@publishing/components/ui/Button';

import s from './StepForm.module.scss';

interface StepFormProps {
  form: ReturnType<typeof useTwoStepLeadForm>;
  intro?: ReactNode;
  nextButtonText?: string;
  step2: FormProps;
  step2Footer?: ReactNode;
  /** 1인실 Tip 노출 여부 (기본 true) */
  showTip?: boolean;
  className?: string;
  classNameStep2?: string;
}

/** Form 의 activeFieldNames memo 유지를 위해 참조 고정 — 렌더마다 새 배열이면 값 prune 이 매번 돈다 */
const STEP2_FIELDS: FormField[] = [
  { type: 'company' },
  { type: 'phone' },
  { type: 'email' },
];

const DEFAULT_INTRO = (
  <>
    <p>
      사무실 예상 인원 수를 입력하고,
      <br />
      최저가 사무실 리스트를 확인해 보세요.
    </p>
    <span>(예: 5명, 20명 등)</span>
  </>
);

export function StepForm({
  form,
  intro = DEFAULT_INTRO,
  nextButtonText = '다음',
  step2,
  step2Footer,
  showTip = true,
  className,
  classNameStep2,
}: StepFormProps) {
  const { people, setPeople, isStep2, hasPeople, handleStep1Submit } = form;
  const {
    className: step2ClassName,
    actionAreaClassName,
    initialValues,
    syncValues,
    ...step2Rest
  } = step2;

  return (
    <div
      className={clsx(
        s.card,
        isStep2 && s.card_step2,
        className,
        isStep2 && classNameStep2,
      )}
    >
      {!isStep2 ? (
        <form className={s.form} onSubmit={handleStep1Submit}>
          <div className={s.intro}>{intro}</div>

          <Input
            type="userCount"
            label="1~999명"
            value={people}
            onChange={setPeople}
            min={PEOPLE_MIN}
            max={PEOPLE_MAX}
          />
          <FormTip
            formKey={FORM_KEY.SHARED_OFFICE}
            values={{ userCount: people }}
            enabled={showTip}
            className={s.form_tip}
            swiper
          />

          <Button
            htmlType="submit"
            size="large"
            width="100%"
            disabled={!hasPeople}
            className={s.next_button}
            icon={false}
            {...gtmClick(GTM_EVENT.NEXT, nextButtonText)}
            data-leadform-name={
              step2Rest.formName ? toBtnName(step2Rest.formName) : undefined
            }
            data-step-number="1"
            data-step-name={toBtnName('사용 인원')}
            data-headcount={people}
          >
            {nextButtonText}
          </Button>
        </form>
      ) : (
        <>
          <Form
            columns={1}
            fields={STEP2_FIELDS}
            leadType={[LEAD_TYPE.SHARED_OFFICE]}
            className={clsx(s.step2_form, step2ClassName)}
            actionAreaClassName={clsx(s.step2_action, actionAreaClassName)}
            initialValues={initialValues}
            syncValues={{ userCount: people, ...syncValues }}
            {...step2Rest}
          />
          {step2Footer}
        </>
      )}
    </div>
  );
}
